import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

const BUNDLE_ID_KEY = '_jv_bundle_id';
const BUNDLE_ROLE_KEY = '_jv_bundle_role';

/** @param {HTMLFormElement} form */
function getProductFormComponent(form) {
  return form.closest('product-form-component');
}

/** @param {string} value */
function cssEscapeAttr(value) {
  const s = String(value);
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Bloco mailbox na PDP: `data-target-section-id` coincide com o buy-buttons.
 * Sticky cart / outras secções usam outro `section.id` mas o mesmo `data-product-id` no product-form.
 *
 * @param {HTMLElement} pfc
 * @returns {Element | null}
 */
function getMailboxBundleRoot(pfc) {
  const sectionId = pfc.dataset.sectionId;
  if (sectionId) {
    const bySection = document.querySelector(
      `jv-mailbox-personalization[data-target-section-id="${cssEscapeAttr(sectionId)}"]`
    );
    if (bySection) return bySection;
  }
  const productId = pfc.dataset.productId;
  if (productId) {
    return document.querySelector(
      `jv-mailbox-personalization[data-product-id="${cssEscapeAttr(productId)}"]`
    );
  }
  return null;
}

function makeBundleId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * @param {HTMLFormElement} form
 * @returns {Record<string, string>}
 */
function collectFormProperties(form) {
  /** @type {Record<string, string>} */
  const out = {};
  const fd = new FormData(form);
  fd.forEach((value, key) => {
    const m = key.match(/^properties\[(.+)]$/);
    if (m && typeof value === 'string' && value.trim() !== '') {
      out[m[1]] = value.trim();
    }
  });
  return out;
}

/**
 * O número do mailbox pode estar ligado a outro `form` via atributo `form=` (buy-buttons).
 * Se o submit vier do sticky/outro form, o FormData desse form não inclui essas propriedades.
 *
 * @param {HTMLFormElement} form
 * @param {Element} bundleRoot
 */
function collectMailboxLineProperties(form, bundleRoot) {
  const out = collectFormProperties(form);
  const addr = bundleRoot.querySelector('[data-jv-mailbox-address-input]');
  if (addr instanceof HTMLInputElement && addr.name) {
    const m = addr.name.match(/^properties\[(.+)]$/);
    if (m) {
      const v = addr.value.trim();
      if (v !== '') out[m[1]] = v;
    }
  }
  return out;
}

/**
 * @param {SubmitEvent} event
 */
async function onSubmitCapture(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

  const pfc = getProductFormComponent(form);
  if (!pfc) return;

  const bundleRoot = getMailboxBundleRoot(pfc);
  if (!bundleRoot) return;

  event.preventDefault();
  // O tema (assets/component.js) delega `on:submit="/handleSubmit"` com `document.addEventListener(..., { capture: true })`.
  // Se o nosso listener estiver só em `document`, pode correr *depois* do handleSubmit → segundo POST (base sem bundle).
  // `window` + capture corre antes de `document` na fase de captura; stopPropagation impede o delegate do tema.
  event.stopPropagation();
  event.stopImmediatePropagation();

  const addToCartTextError = pfc.querySelector('[ref="addToCartTextError"]');
  const addToCartButtonContainer = pfc.querySelector('add-to-cart-component');
  const addBtn = /** @type {HTMLButtonElement | null} */ (
    addToCartButtonContainer?.querySelector('button[type="submit"][name="add"]') || null
  );

  if (addBtn?.disabled) return;

  const quantitySelector = /** @type {any} */ (pfc.querySelector('quantity-selector-component'));
  if (quantitySelector?.canAddToCart) {
    const validation = quantitySelector.canAddToCart();
    if (!validation.canAdd) {
      if (addBtn) addBtn.disabled = true;
      const errorTemplate = pfc.dataset.quantityErrorMax || '';
      const errorMessage = errorTemplate.replace('{{ maximum }}', String(validation.maxQuantity));
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) textNode.textContent = errorMessage;
        else addToCartTextError.appendChild(document.createTextNode(errorMessage));
      }
      setTimeout(() => {
        if (addBtn) addBtn.disabled = false;
      }, 1000);
      return;
    }
  }

  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }

  const variantId = parseInt(String(form.querySelector('input[name="id"]')?.value || ''), 10);
  const quantity = Math.max(1, parseInt(String(new FormData(form).get('quantity') || '1'), 10) || 1);
  if (!variantId) return;

  const bundleId = makeBundleId();
  const baseProps = collectMailboxLineProperties(form, bundleRoot);
  baseProps[BUNDLE_ID_KEY] = bundleId;
  baseProps[BUNDLE_ROLE_KEY] = 'main';

  /** @type {{ id: number; quantity: number; properties: Record<string, string> }[]} */
  const items = [{ id: variantId, quantity, properties: { ...baseProps } }];

  const upgradeSelects = bundleRoot.querySelectorAll('[data-jv-mailbox-upgrade-select]');

  for (const sel of upgradeSelects) {
    if (!(sel instanceof HTMLSelectElement)) continue;
    const opt = sel.selectedOptions[0];
    const vid = opt?.dataset?.variantId;
    if (!vid) continue;
    const vNum = parseInt(vid, 10);
    if (!vNum) continue;
    items.push({
      id: vNum,
      quantity,
      properties: {
        [BUNDLE_ID_KEY]: bundleId,
        [BUNDLE_ROLE_KEY]: 'upgrade',
      },
    });
  }

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  cartItemsComponents.forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  });

  if (addBtn) {
    addBtn.setAttribute('aria-busy', 'true');
    addBtn.disabled = true;
  }

  const body = /** @type {Record<string, unknown>} */ ({
    items,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });

  try {
    const reqConfig = fetchConfig('json', { body: JSON.stringify(body) });
    const res = await fetch(Theme.routes.cart_add_url, {
      ...reqConfig,
      headers: {
        ...reqConfig.headers,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    let data;
    try {
      data = await res.json();
    } catch {
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        const msg = 'Could not add to cart';
        if (textNode) textNode.textContent = msg;
        else addToCartTextError.appendChild(document.createTextNode(msg));
      }
      return;
    }

    if (data.status || data.errors) {
      const message = data.message || data.description || 'Could not add to cart';
      pfc.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message, data.description, data.errors));
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) textNode.textContent = message;
        else addToCartTextError.appendChild(document.createTextNode(message));
      }
      pfc.dispatchEvent(
        new CartAddEvent({}, pfc.id, {
          didError: true,
          source: 'jv-mailbox-personalization',
          itemCount: quantity,
          productId: pfc.dataset.productId,
        })
      );
      return;
    }

    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.removeAttribute('aria-live');
    }

    const liveRegion = pfc.querySelector('[ref="liveRegion"]');
    if (liveRegion) {
      const addedText =
        addBtn?.querySelector('.add-to-cart-text--added')?.textContent?.trim() ||
        (typeof Theme !== 'undefined' && Theme.translations?.added) ||
        'Added';
      liveRegion.textContent = addedText;
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 5000);
    }

    if (addToCartButtonContainer && typeof /** @type {any} */ (addToCartButtonContainer).animateAddToCart === 'function') {
      /** @type {any} */ (addToCartButtonContainer).animateAddToCart();
    }

    /** Linhas visíveis no tema (upgrades JV ficam ocultas no Liquid). */
    let visibleLineCount =
      typeof data.item_count === 'number' ? data.item_count : quantity;
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      if (Array.isArray(cart.items)) {
        visibleLineCount = cart.items.filter(
          /** @param {any} it */ (it) =>
            String(it.properties?.[BUNDLE_ROLE_KEY] ?? '') !== 'upgrade'
        ).length;
      }
      const cartItem = cart.items?.find(
        /** @param {any} it */ (it) => String(it.variant_id) === String(variantId)
      );
      const cartQty = cartItem ? cartItem.quantity : 0;
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }
      const quantityLabel = pfc.querySelector('[ref="quantityLabelCartCount"]');
      if (quantityLabel) {
        const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
        if (inCartText && inCartText[2]) {
          quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
        }
        quantityLabel.classList.toggle('hidden', cartQty === 0);
      }
    } catch {
      /* ignore */
    }

    pfc.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'jv-mailbox-personalization',
        itemCount: visibleLineCount,
        productId: pfc.dataset.productId,
        sections: data.sections,
      })
    );
  } catch (err) {
    console.error(err);
  } finally {
    if (addBtn) {
      addBtn.removeAttribute('aria-busy');
      addBtn.disabled = false;
    }
    cartPerformance.measureFromEvent('add:user-action', event);
  }
}

let captureBound = false;

function ensureCaptureListener() {
  if (captureBound) return;
  captureBound = true;
  window.addEventListener('submit', onSubmitCapture, true);
}

class JvMailboxPersonalization extends HTMLElement {
  connectedCallback() {
    ensureCaptureListener();
    this.#bindAddressCounter();
    this.#bindUpgradePrices();
  }

  #bindUpgradePrices() {
    const sync = (/** @type {HTMLSelectElement} */ sel) => {
      const col = sel.closest('.jv-mailbox-personalization__upgrade-col');
      const priceEl = col?.querySelector('[data-jv-mailbox-group-price]');
      if (!priceEl) return;
      const opt = sel.selectedOptions[0];
      const formatted = opt?.dataset?.priceFormatted;
      if (formatted) {
        priceEl.textContent = `(${formatted})`;
      } else {
        priceEl.textContent = '';
      }
    };

    this.querySelectorAll('select[data-jv-mailbox-upgrade-select]').forEach((sel) => {
      if (!(sel instanceof HTMLSelectElement)) return;
      sync(sel);
      sel.addEventListener('change', () => sync(sel));
    });
  }

  /**
   * @param {string} value
   * @param {string} mode
   */
  #sanitizeAddressValue(value, mode) {
    if (mode === 'digits') return value.replace(/\D/g, '');
    if (mode === 'alphanumeric') return value.replace(/[^A-Za-z0-9 .'&#-]/g, '');
    return value;
  }

  #bindAddressCounter() {
    const input = this.querySelector('[data-jv-mailbox-address-input]');
    const out = this.querySelector('[data-jv-mailbox-counter]');
    if (!(input instanceof HTMLInputElement) || !out) return;
    const template = out.getAttribute('data-template') || '';
    const max = parseInt(out.getAttribute('data-max') || String(input.maxLength || 0), 10) || 0;
    const mode = input.dataset.jvMailboxAddressMode || 'digits';

    // Letters mode: allow spaces and common address punctuation. Overrides the older
    // letters/digits-only `pattern` attribute that the Liquid block may still render.
    if (mode === 'alphanumeric') {
      input.setAttribute('pattern', "[A-Za-z0-9 .'&#-]*");
    }
    
    const update = () => {
      const remaining = Math.max(0, max - input.value.length);
      out.textContent = template.split('[[remaining]]').join(String(remaining));
    };

    const onInput = () => {
      if (mode === 'digits' || mode === 'alphanumeric') {
        const sanitized = this.#sanitizeAddressValue(input.value, mode);
        if (sanitized !== input.value) {
          input.value = sanitized;
        }
      }
      update();
    };

    input.addEventListener('input', onInput);
    onInput();
  }
}

if (!customElements.get('jv-mailbox-personalization')) {
  customElements.define('jv-mailbox-personalization', JvMailboxPersonalization);
}
